import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ClientUpdateApi, {
  ClientUpdateCase,
  ClientUpdateDelivery,
  ClientUpdateRecipient,
} from '../../api/ClientUpdateApi';

/**
 * Send a client a short update without leaving the page (Lloyd, 2 Oct 2026).
 *
 * One kind of update (Lloyd, 8 Oct 2026: "since the new messages will go to email/ text/ portal/ I'm
 * not sure why we need 2 options"). The AI drafts it from the cases ticked below, staff read and edit
 * it, and nothing goes until Send is pressed. It goes by email, text (where the client agreed) and the
 * portal, as ONE message recorded on each ticked case's Activity tab.
 */

interface SendUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Preselected on a client or case page. Without it, the dialog opens with a client search. */
  client?: { guid: string; name: string } | null;
  /**
   * A message written for the occasion, put in the boxes when the dialog opens (the approved-contact
   * permission request, 14 Oct 2026). Staff still read it and press Send; nothing goes by itself.
   */
  initialSubject?: string;
  initialBody?: string;
}

function caseLabel(c: ClientUpdateCase): string {
  return [c.citationNumber || 'No citation number', c.courtName, c.statusName].filter(Boolean).join(' · ');
}

function deliverySummary(d: ClientUpdateDelivery): string {
  const went = [d.email ? `email${d.recipientEmail ? ` (${d.recipientEmail})` : ''}` : null, d.text.sent ? 'text' : null, 'portal']
    .filter(Boolean)
    .join(', ');
  const notText =
    !d.text.sent && d.text.refused === 'no-consent'
      ? ' Not texted: the client has not agreed to texts.'
      : !d.text.sent && d.text.refused === 'no-phone'
        ? ' Not texted: no usable mobile number on file.'
        : '';
  const filed = d.citations.length
    ? ` Recorded on ${d.citations.length === 1 ? 'case' : 'cases'} ${d.citations.join(', ')}.`
    : '';
  return `Sent by ${went}.${notText}${filed}`;
}

const SendUpdateDialog: React.FC<SendUpdateDialogProps> = ({
  open,
  onClose,
  client,
  initialSubject,
  initialBody,
}) => {
  const [recipient, setRecipient] = useState<ClientUpdateRecipient | null>(null);
  const [options, setOptions] = useState<ClientUpdateRecipient[]>([]);
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');
  const [cases, setCases] = useState<ClientUpdateCase[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [contact, setContact] = useState<{ email: string | null; phone: string | null }>({
    email: null,
    phone: null,
  });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [aiDrafted, setAiDrafted] = useState<boolean | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<ClientUpdateDelivery | null>(null);

  const clientGuid = client?.guid;
  const clientName = client?.name;

  // Keyed on the guid and name, not the object, so a parent re-render cannot wipe a draft mid-edit.
  useEffect(() => {
    if (!open) return;
    setRecipient(
      clientGuid ? { User_guid: clientGuid, name: clientName ?? '', email: null, phone: null } : null,
    );
    setOptions([]);
    setTerm('');
    setSubject(initialSubject ?? '');
    setBody(initialBody ?? '');
    setAiDrafted(null);
    setError(null);
    setDelivered(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientGuid, clientName]);

  // The chosen client's open cases, all ticked: one update covers them all unless staff untick one.
  const recipientGuid = recipient?.User_guid;
  useEffect(() => {
    setCases(null);
    setChosen([]);
    if (!open || !recipientGuid) return;
    let cancelled = false;
    ClientUpdateApi.cases(recipientGuid)
      .then((found) => {
        if (cancelled) return;
        setCases(found.cases);
        setChosen(found.cases.map((c) => c.ticketGuid));
        setContact({ email: found.recipientEmail, phone: found.phone });
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Failed to load cases'));
    return () => {
      cancelled = true;
    };
  }, [open, recipientGuid]);

  // Each keystroke searches; only the newest search may fill the list, or a slow earlier reply
  // would replace the results for what was typed after it.
  const latestSearch = useRef(0);
  const search = useCallback(async (value: string): Promise<void> => {
    setTerm(value);
    const searchId = ++latestSearch.current;
    if (value.trim().length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const found = await ClientUpdateApi.search(value.trim());
      if (searchId === latestSearch.current) setOptions(found);
    } catch {
      if (searchId === latestSearch.current) setOptions([]);
    } finally {
      if (searchId === latestSearch.current) setSearching(false);
    }
  }, []);

  const draft = async (): Promise<void> => {
    if (!recipient) return;
    setDrafting(true);
    setError(null);
    try {
      const drafted = await ClientUpdateApi.draft(recipient.User_guid, chosen);
      setSubject(drafted.subject);
      setBody(drafted.body);
      setAiDrafted(drafted.aiDrafted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to draft the update');
    } finally {
      setDrafting(false);
    }
  };

  const send = async (): Promise<void> => {
    if (!recipient) return;
    setSending(true);
    setError(null);
    try {
      setDelivered(await ClientUpdateApi.send(recipient.User_guid, subject.trim(), body.trim(), chosen));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the update');
    } finally {
      setSending(false);
    }
  };

  const chooseClient = (value: ClientUpdateRecipient | null): void => {
    setRecipient(value);
    setSubject('');
    setBody('');
    setAiDrafted(null);
    setError(null);
  };

  const toggleCase = (guid: string) =>
    setChosen((prev) => (prev.includes(guid) ? prev.filter((g) => g !== guid) : [...prev, guid]));

  const unreachable = recipient && cases !== null && !contact.email && !contact.phone;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send update</DialogTitle>
      <DialogContent>
        {delivered ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {deliverySummary(delivered)}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {client ? (
              <Typography>
                <strong>Client:</strong> {client.name}
              </Typography>
            ) : (
              <Autocomplete
                size="small"
                options={options}
                loading={searching}
                value={recipient}
                filterOptions={(x) => x}
                getOptionLabel={(option) =>
                  [option.name, option.email, option.phone].filter(Boolean).join(' · ')
                }
                isOptionEqualToValue={(a, b) => a.User_guid === b.User_guid}
                noOptionsText={term.trim().length < 2 ? 'Type a name, email or phone number' : 'No clients found'}
                onChange={(_event, value) => chooseClient(value)}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') void search(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Name, email or phone"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {recipient && cases === null && !error && <CircularProgress size={20} />}

            {cases !== null && (
              <>
                <Typography variant="subtitle2">
                  {cases.length === 0
                    ? 'This client has no open case, so the update will not be recorded on one.'
                    : 'Covers, and is recorded on, these cases:'}
                </Typography>
                {cases.length > 0 && (
                  <FormGroup>
                    {cases.map((c) => (
                      <FormControlLabel
                        key={c.ticketGuid}
                        control={
                          <Checkbox
                            size="small"
                            checked={chosen.includes(c.ticketGuid)}
                            onChange={() => {
                              toggleCase(c.ticketGuid);
                              // The draft was written about the old selection.
                              setAiDrafted(null);
                            }}
                          />
                        }
                        label={caseLabel(c)}
                      />
                    ))}
                  </FormGroup>
                )}
                <Button
                  variant="outlined"
                  onClick={draft}
                  // With no open case there is nothing to tick, and the short note is still worth sending.
                  disabled={drafting || (cases.length > 0 && chosen.length === 0)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {drafting ? 'Drafting...' : subject || body ? 'Draft again' : 'Draft update'}
                </Button>
              </>
            )}

            {unreachable && (
              <Alert severity="warning">
                This client has no email address or phone number on file, so the update cannot be sent.
              </Alert>
            )}

            {(subject || body) && (
              <>
                {aiDrafted === false && (
                  <Alert severity="info">
                    The AI did not draft this one, so a short note is filled in instead. Edit it as needed.
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                  Goes by{' '}
                  {[contact.email ? `email to ${contact.email}` : null, contact.phone ? 'text (if they agreed to texts)' : null, 'the portal']
                    .filter(Boolean)
                    .join(', ')}
                  .
                </Typography>
                <TextField
                  label="Subject"
                  size="small"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
                <TextField
                  label="Message"
                  multiline
                  minRows={5}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  inputProps={{ maxLength: 5000 }}
                  helperText="Read and edit before sending."
                />
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{delivered ? 'Close' : 'Cancel'}</Button>
        {!delivered && (
          <Button
            variant="contained"
            onClick={send}
            disabled={!recipient || !!unreachable || !subject.trim() || !body.trim() || sending}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendUpdateDialog;
